<template>
  <div :class="`promo-banner promo-banner--${variant}`">
    <i class="promo-banner__close icon-cross" @click="closeHandler"></i>
    <div :class="`promo-banner__container promo-banner__container--${variant}`">
      <div :class="`promo-banner__content`">
        <img
          v-for="(image, index) in imagesToShow"
          :key="index"
          :class="[
            `promo-banner__image`,
            `promo-banner__image--${variant}`,
            { 'promo-banner__image--thin': image.width < 260 },
          ]"
          :src="image.url"
        />

        <div
          v-if="hasHeadline || hasText"
          :class="[
            'promo-banner__text-wrap',
            { 'promo-banner__text-wrap--more-images': imagesToShow.length > 1 },
          ]"
        >
          <h3 v-if="hasHeadline" class="promo-banner__headline">{{ headline }}</h3>
          <div v-if="hasText">{{ text }}</div>

          <div v-if="linkText">
            <a class="promo-banner__link u-arrow-link" :href="linkUrl || false">{{ linkText }}</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'PromoBanner',

  props: {
    variant: {
      type: String,
      default: 'fixed-bottom',
      validator(value) {
        return ['fixed-bottom', 'static-left'].indexOf(value) >= 0
      },
    },
    images: {
      type: Array,
      required: true,
      validator(value) {
        return value.every(obj => !!obj.url && !!obj.width)
      },
    },
    headline: String,
    text: String,
    linkUrl: String,
    linkText: String,
    closeHandler: { type: Function, required: true },
  },

  computed: {
    ...mapGetters({ viewportWidth: 'viewport/width' }),

    isFixedBottom() {
      return this.variant == 'fixed-bottom'
    },

    imagesToShow() {
      const showAll = this.viewportWidth >= On.MediaQueries.width.larger
      const showTwo = this.viewportWidth >= On.MediaQueries.width.large
      const count = showAll ? 3 : showTwo ? 2 : 1

      return this.images.slice(0, count)
    },

    hasHeadline() {
      return this.hasValue(this.headline)
    },

    hasText() {
      return this.hasValue(this.text)
    },
  },

  methods: {
    hasValue(value) {
      return value && /\S/.test(value)
    },
  },
}
</script>

<style scoped lang="scss">
.promo-banner {
  position: relative;

  &--fixed-bottom {
    font-size: 0;
    text-align: center;
  }

  &__container {
    &--static-left {
      padding: #{$site-gutter * 0.75} 0;
    }

    &--fixed-bottom {
      display: inline-block;
      max-width: 900px;
      font-size: $font-size-normal;
      vertical-align: middle;

      @include min-screen($mq-width-larger) {
        padding-right: $site-gutter * 4;
      }

      @include min-screen($mq-width-medium) {
        padding-left: $site-gutter;
        padding-right: $site-gutter * 2;
      }
    }
  }

  &__content {
    display: flex;
    align-items: center;
    max-width: none;
  }

  &__image {
    height: 130px;

    flex-grow: 0;
    flex-shrink: 0;

    &--thin {
      @include max-screen($mq-width-medium) {
        margin-left: 5px;
        margin-right: 5px;
      }
    }

    &--fixed-bottom#{&}--thin {
      margin-top: 5px;
      margin-bottom: 5px;
    }
  }

  &__text-wrap {
    padding: $site-gutter #{$site-gutter * 2} $site-gutter 5px;
    text-align: left;

    @include min-screen($mq-width-medium) {
      padding-left: $site-gutter;
    }

    &--more-images {
      padding-left: $site-gutter * 1.5;
    }
  }

  &__headline {
    margin: 0 0 20px;
    line-height: 1.2;
    font-size: $font-size-larger;

    @include min-screen($mq-width-small) {
      margin: 0 0 10px;
      line-height: 1.1;
      font-size: $font-size-xlarge;
    }
  }

  &__link {
    color: $color-link-lighter;

    &:hover {
      color: $color-link-lighter-hover;
    }
  }

  &__close {
    position: absolute;
    top: 0;
    right: 0;
    margin-right: 0;
    padding: 15px 15px 20px 20px;
    font-size: 12px;
    color: transparentize($color-white, 0.5);
    cursor: pointer;

    &:hover {
      color: transparentize($color-white, 0.2);
    }
  }
}

// Fix broken -ms-flexbox handling...
_:-ms-lang(x),
.promo-banner {
  .promo-banner__content {
    display: block;
  }

  .promo-banner__image {
    float: left;
  }

  .promo-banner__text {
    margin-left: 130px;
  }
}
</style>
